// front/src/i18n/loadTranslations.js

import Api from "../api/Api";

/**
 * Simple i18n loader (backend-driven)
 *
 * Responsibilities:
 * - Read current language from localStorage
 * - Fetch translations from backend: GET /api/v1/i18n/{lang}/
 * - Cache translations in-memory (per page load)
 *
 * Notes:
 * - Backend is the source of truth
 * - Frontend only consumes translation keys
 * - No external i18n libraries are used (by design)
 */

const STORAGE_KEY = "lang";
const DEFAULT_LANG = "fa";

// In-memory cache (per page load)
const cache = new Map();

function normalizeLang(value) {
  const v = String(value || "").trim().toLowerCase();
  if (v === "fa" || v === "en") return v;
  return DEFAULT_LANG;
}

/**
 * Get current language.
 * Priority:
 * 1) localStorage
 * 2) fallback to "fa"
 */
export function getCurrentLanguage() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? normalizeLang(stored) : DEFAULT_LANG;
  } catch {
    return DEFAULT_LANG;
  }
}

/**
 * Set language and persist it.
 * This does NOT reload the page automatically.
 */
export function setCurrentLanguage(lang) {
  try {
    localStorage.setItem(STORAGE_KEY, normalizeLang(lang));
  } catch {
    // ignore
  }
}

/**
 * Load translations for a given language (or current language).
 *
 * Backend endpoint:
 * - Api baseURL already includes "/api"
 * - So we call: /v1/i18n/{lang}/
 *
 * Returns backend payload:
 * { lang, version, translations: { key: value } }
 */
export async function loadTranslations(lang) {
  const safeLang = normalizeLang(lang || getCurrentLanguage());

  // Avoid refetch if already loaded
  if (cache.has(safeLang)) {
    return cache.get(safeLang);
  }

  try {
    const { data } = await Api.get(`/v1/i18n/${safeLang}/`);

    const payload = {
      lang: data?.lang || safeLang,
      version: data?.version || "v1",
      translations: data?.translations && typeof data.translations === "object" ? data.translations : {},
    };

    cache.set(safeLang, payload);
    return payload;
  } catch (error) {
    // Fail-safe: keep app running even if i18n fails
    const payload = { lang: safeLang, version: "v1", translations: {} };
    cache.set(safeLang, payload);
    return payload;
  }
}
